import React, { Component } from "react";
import { Modal, Table, Button, Row, Col, Input, message, Spin, Tooltip } from "antd";
import { DownloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { getSettlementDetailsAPI, markSettledAPI } from "../../Graphs/Report/subAdminSettlementAPI";
import { formatePrice } from "../../utils/general";
import { alertError } from "../../utils/alert";
import PayableBreakdownModal from "./PayableBreakdownModal";

class SettlementDetailsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      isSubmitting: false,
      settlement: null,
      paymentReference: "",
      settlementNotes: "",
      showMarkSettledForm: false,
      isBreakdownModalOpen: false,
    };
  }

  openBreakdownModal = () => {
    this.setState({ isBreakdownModalOpen: true });
  };

  closeBreakdownModal = () => {
    this.setState({ isBreakdownModalOpen: false });
  };

  componentDidMount() {
    if (this.props.visible && this.props.settlementId) {
      this.fetchDetails();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.visible &&
      this.props.settlementId &&
      (!prevProps.visible || this.props.settlementId !== prevProps.settlementId)
    ) {
      this.fetchDetails();
    }
  }

  fetchDetails = async () => {
    try {
      this.setState({ isLoading: true, showMarkSettledForm: false });
      const { authToken, settlementId } = this.props;
      const res = await getSettlementDetailsAPI(authToken, settlementId);

      if (res && res.status === 200 && res.data) {
        this.setState({ settlement: res.data });
      } else {
        throw new Error((res && res.message) || "Failed to load settlement details");
      }
    } catch (err) {
      alertError("SETTLEMENT DETAILS", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleMarkSettled = async () => {
    const { paymentReference, settlementNotes, settlement } = this.state;
    if (!paymentReference || !paymentReference.trim()) {
      message.error("Please provide a Payment Reference (bank transfer / receipt number).");
      return;
    }

    try {
      this.setState({ isSubmitting: true });
      const { authToken, onSettledSuccess } = this.props;
      const res = await markSettledAPI(authToken, {
        settlementId: settlement.id,
        paymentReference: paymentReference.trim(),
        settlementNotes: settlementNotes.trim(),
      });

      if (res && res.status === 200) {
        message.success("Settlement statement successfully marked as settled!");
        await this.fetchDetails();
        if (onSettledSuccess) {
          onSettledSuccess();
        }
      } else {
        throw new Error((res && res.message) || "Failed to mark settlement as settled");
      }
    } catch (err) {
      alertError("SETTLEMENT", err);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  exportToCSV = () => {
    const { settlement } = this.state;
    if (!settlement || !settlement.items || settlement.items.length === 0) {
      message.info("No items to export.");
      return;
    }

    const headers = [
      "Card ID",
      "Card Name",
      "Source",
      "Quantity",
      "Unit Cost (LYD)",
      "Total Cost (LYD)",
      "Unit Retail (LYD)",
      "Total Retail (LYD)",
      "Profit (LYD)",
      "Cost Fallback?",
    ];

    const rows = settlement.items.map((item) => [
      `"${item.card_id || ""}"`,
      `"${(item.card_name || "").replace(/"/g, '""')}"`,
      `"${item.source || ""}"`,
      item.quantity,
      formatePrice(item.unit_cost),
      formatePrice(item.total_cost),
      formatePrice(item.unit_retail_price),
      formatePrice(item.total_retail_price),
      formatePrice(item.profit),
      item.is_cost_fallback ? "YES" : "NO",
    ]);

    const csvContent = [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `settlement_${settlement.city}_${settlement.period_label}_items.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  renderSourceTag = (source) => {
    switch (source) {
      case "borrowed_admin":
        return <span className="settlement-source-chip chip-borrowed">Admin</span>;
      case "own_sub_admin":
        return <span className="settlement-source-chip chip-own">Own stock</span>;
      case "mint_api":
        return <span className="settlement-source-chip chip-mint">Mint API</span>;
      case "like_api":
        return <span className="settlement-source-chip chip-like">Like API</span>;
      case "khales_api":
        return <span className="settlement-source-chip chip-khales">Khales API</span>;
      case "company":
        return <span className="settlement-source-chip chip-company">Company</span>;
      default:
        return <span className="settlement-source-chip chip-default">{source}</span>;
    }
  };

  itemColumns = [
    {
      title: "Sr. No.",
      key: "idx",
      width: 60,
      align: "center",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Card Name",
      dataIndex: "card_name",
      key: "card_name",
      render: (name) => <span className="item-card-name">{name || "-"}</span>,
    },
    {
      title: "Inventory Source",
      dataIndex: "source",
      key: "source",
      width: 170,
      render: (source) => this.renderSourceTag(source),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 65,
      align: "right",
    },
    {
      title: "Unit Cost",
      dataIndex: "unit_cost",
      key: "unit_cost",
      width: 95,
      align: "right",
      render: (val) => `${formatePrice(val)} LYD`,
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      width: 105,
      align: "right",
      render: (val) => `${formatePrice(val)} LYD`,
    },
    {
      title: "Unit Retail",
      dataIndex: "unit_retail_price",
      key: "unit_retail_price",
      width: 95,
      align: "right",
      render: (val) => `${formatePrice(val)} LYD`,
    },
    {
      title: "Total Retail",
      dataIndex: "total_retail_price",
      key: "total_retail_price",
      width: 105,
      align: "right",
      render: (val) => `${formatePrice(val)} LYD`,
    },
    {
      title: "Branch Profit",
      dataIndex: "profit",
      key: "profit",
      width: 110,
      align: "right",
      render: (val) => (
        <span style={{ color: Number(val) >= 0 ? "#16a34a" : "#dc2626" }}>
          {formatePrice(val)} LYD
        </span>
      ),
    },
    {
      title: "Audit Status",
      dataIndex: "is_cost_fallback",
      key: "is_cost_fallback",
      width: 125,
      align: "center",
      render: (flag) =>
        flag ? (
          <span className="settlement-audit-chip chip-fallback">Fallback Applied</span>
        ) : (
          <span className="settlement-audit-chip chip-direct">Direct Cost</span>
        ),
    },
  ];

  render() {
    const { visible, onClose, isAdmin } = this.props;
    const {
      settlement,
      isLoading,
      isSubmitting,
      showMarkSettledForm,
      paymentReference,
      settlementNotes,
    } = this.state;

    return (
      <Modal
        wrapClassName="settlement-details-modal"
        title={
          <div className="settlement-modal-header-wrapper">
            <div className="settlement-modal-title-row">
              <span className="settlement-modal-title">
                Branch Settlement Statement & Itemized Drilldown
              </span>
              {settlement && (
                <span
                  className={`settlement-status-badge badge-${
                    settlement.status || "pending"
                  }`}
                >
                  {settlement.status
                    ? settlement.status.charAt(0).toUpperCase() + settlement.status.slice(1)
                    : "Pending"}
                </span>
              )}
            </div>
            {settlement && (
              <div className="settlement-modal-subtitle">
                <span>
                  {(settlement.user_city && settlement.user_city.name) ||
                    settlement.city ||
                    "Branch"}
                </span>
                <span className="modal-dot">•</span>
                <span>{settlement.period_label || ""}</span>
                {settlement.sub_admin && (
                  <>
                    <span className="modal-dot">•</span>
                    <span>
                      {settlement.sub_admin.first_name}{" "}
                      {settlement.sub_admin.last_name || ""}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        }
        visible={visible}
        onCancel={onClose}
        width={1100}
        footer={[
          <Button
            key="export"
            className="btn-modal-export"
            icon={<DownloadOutlined />}
            onClick={this.exportToCSV}
            disabled={!settlement}
          >
            Export CSV
          </Button>,
          <Button
            key="close"
            type="primary"
            className="btn-modal-close"
            onClick={onClose}
          >
            Close
          </Button>,
        ]}
      >
        {isLoading || !settlement ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" tip="Loading settlement statement and audit details..." />
          </div>
        ) : (() => {
          const items = (settlement && settlement.items) || [];
          let mintApiCount = 0;
          let mintApiCost = 0;
          let likeApiCount = 0;
          let likeApiCost = 0;
          let khalesApiCount = 0;
          let khalesApiCost = 0;

          items.forEach((item) => {
            if (item.source === "mint_api") {
              mintApiCount += Number(item.quantity || 0);
              mintApiCost += Number(item.total_cost || 0);
            } else if (item.source === "like_api") {
              likeApiCount += Number(item.quantity || 0);
              likeApiCost += Number(item.total_cost || 0);
            } else if (item.source === "khales_api") {
              khalesApiCount += Number(item.quantity || 0);
              khalesApiCost += Number(item.total_cost || 0);
            }
          });

          if (mintApiCount === 0 && likeApiCount === 0 && khalesApiCount === 0 && Number(settlement?.api_cards_count || 0) > 0) {
            mintApiCount = Number(settlement.api_cards_count || 0);
            mintApiCost = Number(settlement.api_cards_cost_total || 0);
          }

          return (
          <div>
            {/* High-Level Overview Cards */}
            <div className="modal-kpi-grid modal-kpi-grid-overview">
              <div className="modal-kpi-card">
                <div className="modal-kpi-label">Total Cards</div>
                <div className="modal-kpi-value">
                  {settlement.total_cards_count || 0}{" "}
                  <span className="modal-kpi-unit">cards</span>
                </div>
                <div className="modal-kpi-subtext">
                  {settlement.own_cards_count || 0} own branch stock
                </div>
              </div>

              <div className="modal-kpi-card">
                <div className="modal-kpi-label">Sub-Admin Total Sell</div>
                <div className="modal-kpi-value">
                  {formatePrice(settlement.total_retail_sales)}{" "}
                  <span className="modal-kpi-unit">LYD</span>
                </div>
                <div className="modal-kpi-subtext">
                  Total retail sales
                </div>
              </div>

              <div className="modal-kpi-card modal-kpi-card-highlight">
                <div className="modal-kpi-label-row">
                  <span className="modal-kpi-label">Amount Payable</span>
                  <Tooltip title="View breakdown (Admin, Mint API, Like API, Khales API)">
                    <PayableBreakdownModal
                      settlement={settlement}
                      authToken={this.props.authToken}
                      placement="bottomRight"
                    >
                      <Button
                        type="text"
                        size="small"
                        className="settlement-breakdown-icon-btn"
                        icon={<InfoCircleOutlined />}
                        aria-label="View breakdown"
                      />
                    </PayableBreakdownModal>
                  </Tooltip>
                </div>
                <div className="modal-kpi-value modal-kpi-payable">
                  {formatePrice(settlement.net_payable_to_admin)}{" "}
                  <span className="modal-kpi-unit">LYD</span>
                </div>
                <div className="modal-kpi-subtext">
                  {(Number(settlement.borrowed_cards_count || 0) + Number(settlement.api_cards_count || 0)).toLocaleString()} cards from Admin
                </div>
              </div>

              <div className="modal-kpi-card">
                <div className="modal-kpi-label">Sub-Admin Gross Profit</div>
                <div className="modal-kpi-value">
                  {formatePrice(
                    Number(settlement.borrowed_cards_profit_total || 0) +
                      Number(settlement.own_cards_profit_total || 0)
                  )}{" "}
                  <span className="modal-kpi-unit">LYD</span>
                </div>
                <div className="modal-kpi-subtext">
                  Retained branch gross profit
                </div>
              </div>
            </div>

            {/* Statement Metadata */}
            <div className="modal-metadata-card">
              <div className="metadata-grid">
                <div className="metadata-item">
                  <span className="metadata-label">Branch city</span>
                  <span className="metadata-value">
                    {(settlement.user_city && settlement.user_city.name) ||
                      settlement.city ||
                      "-"}
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Period</span>
                  <span className="metadata-value">
                    {settlement.period_type
                      ? settlement.period_type.charAt(0).toUpperCase() +
                        settlement.period_type.slice(1)
                      : "-"}{" "}
                    ({settlement.period_label || "-"})
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Sub-admin user</span>
                  <span className="metadata-value">
                    {settlement.sub_admin
                      ? `${settlement.sub_admin.first_name} ${
                          settlement.sub_admin.last_name || ""
                        } (${settlement.sub_admin.email})`
                      : "Unassigned"}
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Third-party API</span>
                  <span className="metadata-value">
                    {formatePrice(settlement.api_cards_cost_total)} LYD (
                    {settlement.api_cards_count || 0} cards)
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Payment status</span>
                  <span className="metadata-value">
                    <span
                      className={`settlement-status-badge badge-${
                        settlement.status || "pending"
                      }`}
                    >
                      {settlement.status
                        ? settlement.status.charAt(0).toUpperCase() +
                          settlement.status.slice(1)
                        : "Pending"}
                    </span>
                    {settlement.status === "settled" && settlement.payment_reference ? (
                      <span className="metadata-ref">
                        Ref: {settlement.payment_reference}
                      </span>
                    ) : null}
                  </span>
                </div>
              </div>
            </div>

            {/* Automated Sanity Checks Banner */}
            <div className="modal-audit-banner-wrap">
              {settlement.is_flagged ? (
                <div className="modal-audit-banner banner-flagged">
                  <div className="banner-top">
                    <ExclamationCircleOutlined className="banner-icon" />
                    <span className="banner-heading">
                      Flagged: {settlement.flag_count} discrepancy check(s) detected
                    </span>
                  </div>
                  <div className="banner-content">
                    <div className="discrepancy-items">
                      {(settlement.discrepancy_details || []).map((d, i) => (
                        <div key={i} className="discrepancy-row">
                          <span className="discrepancy-code">
                            [{d.severity || "Warning"}] {d.code}
                          </span>
                          <span className="discrepancy-msg">{d.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="modal-audit-banner banner-clean">
                  <CheckCircleOutlined className="banner-icon" />
                  <span className="banner-heading">Verified</span>
                  <span className="banner-desc">
                    All 4 automated sanity checks passed with zero discrepancies.
                  </span>
                </div>
              )}
            </div>

            {/* Admin Settlement Action Panel */}
            {isAdmin && settlement.status !== "settled" && (
              <div className="modal-reconciliation-card">
                <div className="reconciliation-top">
                  <div>
                    <div className="reconciliation-title">
                      Record settlement payment
                    </div>
                    {!showMarkSettledForm && (
                      <div className="reconciliation-desc">
                        Record payment reference to mark this statement as settled.
                      </div>
                    )}
                  </div>
                  {!showMarkSettledForm && (
                    <Button
                      type="primary"
                      className="btn-record-settlement"
                      onClick={() => this.setState({ showMarkSettledForm: true })}
                    >
                      Record payment
                    </Button>
                  )}
                </div>

                {showMarkSettledForm && (
                  <div className="reconciliation-form-wrap">
                    <Row gutter={16}>
                      <Col span={10}>
                        <label className="field-label">Payment reference *</label>
                        <Input
                          className="field-input"
                          placeholder="e.g. Bank wire #98214, cash voucher #12"
                          value={paymentReference}
                          onChange={(e) => this.setState({ paymentReference: e.target.value })}
                        />
                      </Col>
                      <Col span={10}>
                        <label className="field-label">Audit notes (optional)</label>
                        <Input
                          className="field-input"
                          placeholder="e.g. Verified by bank statement on Sept 9"
                          value={settlementNotes}
                          onChange={(e) => this.setState({ settlementNotes: e.target.value })}
                        />
                      </Col>
                      <Col span={4} className="field-actions-col">
                        <Button
                          type="primary"
                          className="btn-confirm-settlement"
                          loading={isSubmitting}
                          onClick={this.handleMarkSettled}
                        >
                          Confirm
                        </Button>
                        <Button
                          className="btn-cancel-settlement"
                          onClick={() => this.setState({ showMarkSettledForm: false })}
                        >
                          Cancel
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            )}

            {/* Itemized Cards Table */}
            <div className="modal-table-card">
              <div className="table-card-header">
                <span className="table-card-title">Itemized Card Breakdown</span>
                <span className="table-card-count">
                  {settlement.items ? settlement.items.length : 0} items
                </span>
              </div>
              <Table
                dataSource={settlement.items || []}
                columns={this.itemColumns}
                rowKey="id"
                size="small"
                pagination={{
                  pageSize: 8,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  size: "small",
                }}
                className="settlement-items-table"
              />
            </div>
          </div>
          );
        })()}
      </Modal>
    );
  }
}

export default SettlementDetailsModal;
